import { useRouter } from 'next/navigation'
import * as React from 'react'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { CreateBranchParams } from '@basehub/data/replicache/mutators/repo-space/branch/mutators'
import { toast } from 'sonner'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useReplicacheParamsStore } from '~/replicache/frontend/init'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { useUserStore } from '~/app/_store/user-store'

export const useCreateBranchMutator = () => {
  const { data } = useRepoReplicache()
  const router = useRouter()
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const authorId = useUserStore((s) => s.optimisticUser?.id)

  return React.useCallback(
    async ({
      checkout,
      toastSuccess,
      toastError,
      ...params
    }: CreateBranchParams & {
      checkout: boolean
      toastSuccess: boolean
      toastError: boolean
    }) => {
      console.log('[useCreateBranchMutator] Starting branch creation', {
        checkout,
        toastSuccess,
        toastError,
        branchName: params.data.name,
        branchId: params.data.id,
        baseBranchOrCommitId: params.baseBranchOrCommitId,
        idSeed: params.idSeed,
        authorId,
        gitBranch: params.data.git?.branch,
      })

      if (!data?.replicache) {
        console.log('[useCreateBranchMutator] No replicache instance, aborting')
        return
      }

      const createBranchParams = authorId
        ? { ...params, data: { ...params.data, authorId } }
        : params

      console.log('[useCreateBranchMutator] Calling createBranch_DONT_USE_DIRECTLY', {
        createBranchParams,
      })

      const { branch: newBranch } = (await data.replicache.mutate
        .createBranch_DONT_USE_DIRECTLY(createBranchParams)
        .then((result) => {
          console.log('[useCreateBranchMutator] createBranch_DONT_USE_DIRECTLY succeeded', {
            newBranchId: result.branch.id,
            newBranchName: result.branch.name,
            baseBranchOrCommitId: result.baseBranchOrCommit?.id,
          })
          return result
        })
        .catch((err) => {
          console.error('[useCreateBranchMutator] createBranch_DONT_USE_DIRECTLY failed', {
            error: err,
            errorMessage: err?.message,
            errorType: err?.constructor?.name,
          })
          if (!toastError) throw err
          if (err instanceof MutatorUserError) toast.error(err.reason)
          else {
            console.error(err)
            toast.error('An error occurred while creating the branch')
          }
        })) ?? { branch: null }

      if (!newBranch) {
        console.log('[useCreateBranchMutator] No new branch returned, aborting')
        return null
      }

      console.log('[useCreateBranchMutator] Branch created successfully', {
        newBranchId: newBranch.id,
        newBranchName: newBranch.name,
        workingRootBlockId: newBranch.workingRootBlockId,
        headCommitId: newBranch.headCommitId,
      })

      window.sessionStorage.setItem?.(
        `bshb_recently_created_branch-${data.spaceId}`,
        newBranch.name
      )
      useReplicacheParamsStore.setState((p) => {
        return { ...p, repo: { ...p.repo, branchOrCommit: newBranch.name } }
      })

      if (checkout) {
        console.log('[useCreateBranchMutator] Checking out new branch', {
          newBranchName: newBranch.name,
          targetUrl: `/${teamSlug}/${repoSlug}/${repoPage}/${encodeURIComponent(newBranch.name)}`,
        })
        router.push(
          `/${teamSlug}/${repoSlug}/${repoPage}/${encodeURIComponent(
            newBranch.name
          )}`
        )
      }

      if (toastSuccess) {
        if (checkout) toast.success('Branch created, checking it out...')
        else toast.success('Branch created.')
      }

      console.log('[useCreateBranchMutator] Branch creation complete', {
        newBranchId: newBranch.id,
        newBranchName: newBranch.name,
      })

      return newBranch
    },
    [
      authorId,
      data?.replicache,
      data?.spaceId,
      repoPage,
      repoSlug,
      router,
      teamSlug,
    ]
  )
}
